/**
 * CustomerTypeFilter Component
 * Dropdown filter for selecting customer type (B2B, B2C, or All)
 * Uses Radix UI Select for accessible dropdown
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 2
 */

import * as React from 'react';
import { Building2, User, Users } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { CustomerType } from '../types/filter.types';

// ============================================
// CONSTANTS
// ============================================

/**
 * Customer type configuration
 */
const CUSTOMER_TYPE_CONFIG: Record<CustomerType, { label: string; description: string; icon: React.ReactNode }> = {
  ALL: {
    label: 'All Customers',
    description: 'Show both B2B and B2C orders',
    icon: <Users className="h-4 w-4" />,
  },
  B2B: {
    label: 'Business (B2B)',
    description: 'Bulk orders and contracts',
    icon: <Building2 className="h-4 w-4" />,
  },
  B2C: {
    label: 'Consumer (B2C)',
    description: 'Individual customer orders',
    icon: <User className="h-4 w-4" />,
  },
};

/**
 * Display order for customer types
 */
const CUSTOMER_TYPE_ORDER: CustomerType[] = ['ALL', 'B2B', 'B2C'];

// ============================================
// INTERFACE
// ============================================

export interface CustomerTypeFilterProps {
  selectedCustomerType: CustomerType;
  onCustomerTypeChange: (type: CustomerType) => void;
  className?: string;
}

// ============================================
// COMPONENT
// ============================================

export const CustomerTypeFilter = React.memo(function CustomerTypeFilter({
  selectedCustomerType,
  onCustomerTypeChange,
  className,
}: CustomerTypeFilterProps) {
  // Handle customer type change
  const handleValueChange = React.useCallback(
    (value: string) => {
      onCustomerTypeChange(value as CustomerType);
    },
    [onCustomerTypeChange]
  );

  const selectedConfig = CUSTOMER_TYPE_CONFIG[selectedCustomerType];

  return (
    <div className={cn('space-y-2', className)}>
      {/* Label */}
      <label
        htmlFor="customer-type-select"
        className="text-sm font-medium text-foreground"
      >
        Customer
      </label>

      {/* Select Dropdown */}
      <Select
        value={selectedCustomerType}
        onValueChange={handleValueChange}
      >
        <SelectTrigger
          id="customer-type-select"
          className="w-full h-9 px-3 py-2 text-sm"
          aria-label="Filter by customer type"
        >
          <SelectValue placeholder="All Customers" />
        </SelectTrigger>
        <SelectContent>
          {CUSTOMER_TYPE_ORDER.map((type) => {
            const config = CUSTOMER_TYPE_CONFIG[type];
            return (
              <SelectItem
                key={type}
                value={type}
                className="cursor-pointer"
              >
                <div className="flex items-start gap-3 py-1">
                  <div className="mt-0.5">{config.icon}</div>
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium">{config.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {config.description}
                    </span>
                  </div>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {/* Accessibility hint */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {selectedCustomerType === 'ALL'
          ? 'Showing all customer types'
          : `Filtering by ${selectedConfig.label} only`}
      </span>
    </div>
  );
});
